import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPostDetails, updatePostStatus } from '../redux/ReportedPostsSlice';
import { Check, X, Flag, AlertTriangle, ArrowLeft, Text } from 'lucide-react';
import { Oval } from 'react-loader-spinner';
import { toast } from 'react-toastify';

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
    const [isUpdating, setIsUpdating] = useState(false);
  
  const { selectedPost: post, loading } = useSelector(state => state.reportedPosts);

  useEffect(() => {
    if (id) {
      dispatch(fetchPostDetails(id));
    }
  }, [dispatch, id]);

  if (loading) {
    return (
        <div className="flex justify-center items-center h-screen">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
        </div>
    )
}

  if (!post) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate('/dashboard/reported-posts')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Reports
        </button>
        <div className="text-center py-12">
          <AlertTriangle size={48} className="mx-auto text-yellow-500 mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Post Not Found</h2>
          <p className="text-gray-600">This reported post might have been removed or doesn't exist.</p>
        </div>
      </div>
    );
  }

  const handleAction = async (status) => {
    try {
      setIsUpdating(true)
      const result = await dispatch(updatePostStatus({ postId: post.id, status })).unwrap();
    // Check if the update was successful
    if (result) {
      toast.success('Post updated')
    }
    } catch (error) {
      console.error('Failed to update post status:', error);
    }finally{
      setIsUpdating(false)
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-800',
      retained: 'bg-green-100 text-green-800',
      removed: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm ${statusClasses[status]}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className=" sm:p-6 max-w-4xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate('/dashboard/reported-posts')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} className="mr-2" />
        Back to Reports
      </button>

      {/* Main Content Card */}
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header Section */}
        <div className="p-6 border-b">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-4">
              <img
                src={post?.post?.created_by.profile_img}
                alt={`${post?.post?.created_by.first_name} ${post?.post?.created_by.last_name}`}
                className="w-12 h-12 rounded-full"
              />
              <div>
                <h1 className="text-xl font-semibold">
                  {`${post?.post?.created_by.first_name} ${post?.post?.created_by.last_name}`}
                </h1>
                <p className="text-sm text-gray-500">{formatDate(post?.created_at)}</p>
              </div>
            </div>
            {getStatusBadge(post?.status)}
          </div>

          {/* Action Buttons */}
          {post?.status === 'pending' && (
            <div className="flex space-x-3">
              <button
                onClick={() => handleAction('retained')}
                className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <Check size={20} className="mr-2" />
                {isUpdating? (
                  <div className=" flex justify-center items-center">
                    <Oval
                      visible={true}
                      height="20"
                      width="20"
                      color="#ffff"
                      ariaLabel="oval-loading"
                      wrapperStyle={{}}
                      wrapperClass=""
                    />
                  </div>
                ) : 'Keep Post'}
              </button>
              <button
                onClick={() => handleAction('removed')}
                className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                <X size={20} className="mr-2" />
                  {isUpdating ? (
                              <div className=" flex justify-center items-center">
                                <Oval
                                  visible={true}
                                  height="20"
                                  width="20"
                                  color="#ffff"
                                  ariaLabel="oval-loading"
                                  wrapperStyle={{}}
                                  wrapperClass=""
                                />
                              </div>
                            ) : 'Remove Post'}
              </button>
            </div>
          )}
        </div>

        {/* Post Content */}
        <div className="p-2 sm:p-6 border-b">
          <h2 className="text-lg font-semibold mb-3">Post Content</h2>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-800 whitespace-pre-wrap">{post?.post?.content}</p>
            {post?.post?.images && post?.post?.images.length > 0 && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {post?.post?.images.map((image) => (
                  <img
                    key={image.id}
                    src={image.image_url}
                    alt="Post content"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Reporter Information */}
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold mb-3">Reported By</h2>
          <div className="flex items-center space-x-4">
            <img
              src={post?.reported_by?.profile_img}
              alt={`${post?.reported_by?.first_name} ${post?.reported_by?.last_name}`}
              className="w-10 h-10 rounded-full"
            />
            <div>
              <p className="font-medium">
                {`${post?.reported_by?.first_name} ${post?.reported_by?.last_name}`}
              </p>
              <p className="text-sm text-gray-500">{formatDate(post?.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Report Reason */}
        <div className="p-6">
          <div className="flex items-center mb-4">
            <Flag size={20} className="text-red-500 mr-2" />
            <h2 className="text-lg font-semibold">Report Reason</h2>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-800">{post?.reason}</p>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center mb-4">
            <Text size={20} className="text-red-500 mr-2" />
            <h2 className="text-lg font-semibold">Report Description</h2>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-800">{post?.description}</p>
          </div>
        </div>
        {/* {post.status === 'pending' &&
          (<div className="flex space-x-3">
            <button onClick={() => handleAction('reviewed')} className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors" >
              <Check size={20} className="mr-2" />
              Keep Post
            </button>
            <button onClick={() => handleAction('removed')} className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors" >
              <X size={20} className="mr-2" />
              Remove Post
            </button>
          </div>)} */}
      </div>
    </div>
  );
};

export default PostDetail;